import { UploadFile } from 'antd';
import React, { FC, useEffect, useState } from 'react';
import EmployeeThumb from '../../components/EmployeeThumb';
import CommentForm from '../../components/forms/ticket/CommentForm';
import CommentList from '../../components/lists/CommentList';
import EmployeeThumbLoading from '../../components/loading/EmployeeThumbLoading';
import Tabs from '../../components/tabs/Tabs';
import Tag from '../../components/tags/Tag';
import DateRange from '../../components/typography/DateRange';
import Typography from '../../components/typography/Typography';
import { useCompany } from '../../hooks/useCompany';
import { useUser } from '../../hooks/useUser';
import { DefaultAttachment } from '../../interfaces/Attachments';
import { DefaultCommentInterface } from '../../interfaces/Comments';
import { ProjectTaskDetailInterface } from '../../interfaces/Projects';
import { DefaultEmployeeInterface } from '../../interfaces/UserInterface';
import { AttachmentService } from '../../services/attachments/AttachmentService';
import { CommentService } from '../../services/comments/CommentService';
import { ProjectsService } from '../../services/project/Projects';
import { UsersService } from '../../services/users/Users';
import { mapToProjectTaskComment } from '../../utils/mappers/CommentMapper';
import { getInitials } from '../../utils/text/GetInitials';

interface Props {
  data: ProjectTaskDetailInterface;
}

const ProjectTaskDetail: FC<Props> = ({ data }) => {
  const projectService = new ProjectsService();
  const userService = new UsersService();
  const attachmentService = new AttachmentService();
  const commentService = new CommentService();
  const user = useUser();
  const { companyId, companyName } = useCompany();

  const [comments, setComments] = useState<DefaultCommentInterface[]>([]);
  const [employee, setEmployee] = useState<DefaultEmployeeInterface>();
  const [loading, setLoading] = useState<boolean>();
  const fetchEmployeeDetails = async () => {
    if (!data.task.employeeId) {
      return null;
    }
    try {
      const userDetails = await userService.getEmployeeById(
        data.task.employeeId
      );
      setEmployee(userDetails);
      return userDetails;
    } catch (error) {
      console.error('Error fetching employee details:', error);
      return null;
    }
  };

  console.log(user);

  const fetchComments = async () => {
    try {
      const comments = await projectService.getTaskComments(
        data.task.phase_id,
        data.task.rule_id,
        data.projectId,
        data.companyId
      );
      setComments(comments);
      console.log('comments', comments);
      return comments;
    } catch (error) {
      console.error('Error fetching comments:', error);
      return [];
    }
  };

  const handleComment = async (comment: string, files: UploadFile[]) => {
    setLoading(true);
    const commentData: Partial<DefaultCommentInterface> =
      mapToProjectTaskComment({
        body: comment,
        userId: user.userId,
        userName: getInitials(user.firstName, user.lastName),
        taskId: data.task.rule_id,
        projectId: data.projectId,
        company: companyName,
        attachments: files.length,
      });

    const newComment = await commentService.addProjectComment(
      companyId,
      data.projectId,
      data.task.phase_id,
      data.task.rule_id,
      commentData
    );

    const commentId = newComment.comment[0];
    const uploadPromises = files.map(async (fileItem) => {
      const filename = `${fileItem.uid}-${fileItem.name}`;

      const signedUrl = await attachmentService.getUploadUrl(
        filename,
        fileItem.type,
        1
      );

      await attachmentService.upload(
        signedUrl,
        fileItem as unknown as File,
        fileItem.type
      );

      return filename;
    });

    const filenames = await Promise.all(uploadPromises);

    const attachments = filenames.map(async (filename) => {
      const attachment: DefaultAttachment = {
        bij_bijlage: filename,
        bij_com_id: commentId,
        bij_proj_id: data.projectId,
      };

      return attachmentService.addAttachment(
        companyId,
        data.projectId,
        attachment
      );
    });

    const uploaded = await Promise.all(attachments);
    setComments((prev) => [
      ...prev,
      {
        com_id: commentId,
        com_commentaar: commentData.com_commentaar ?? '',
        com_geb_naam: commentData.com_geb_naam ?? null,
        com_type: commentData.com_type ?? null,
        created_at: new Date(),
        updated_at: new Date(),
        com_project_id: commentData.com_project_id ?? null,
        com_geb_id: commentData.com_geb_id ?? null,
        attachments: uploaded,
      },
    ]);

    setLoading(false);
  };

  const tabs = [
    {
      nav: 'Opmerkingen',
      component: (
        <CommentList
          comments={comments}
          comment_count={data.task.comments_count || 0}
        />
      ),
    },
  ];

  useEffect(() => {
    fetchEmployeeDetails();
    fetchComments();
  }, [data]);

  return (
    <div className="flex flex-col min-h-[calc(100vh-160px)] md:min-h-[calc(100vh-160px)]">
      {' '}
      <div className="mt-4 flex flex-wrap items-center gap-x-4 gap-y-2">
        {employee ? (
          <div className="flex items-center gap-x-4 mr-4">
            {employee?.med_foto && (
              <EmployeeThumb
                image={employee.med_foto}
                name={employee.med_naam}
                info={{
                  title: 'Verantwoordelijke',
                  subtitle: employee.med_naam,
                }}
                addClass="lg:w-10 lg:h-10 hover:scale-100 hover:outline outline-2 outline-offset-2"
              />
            )}
          </div>
        ) : (
          <EmployeeThumbLoading showInfo />
        )}

        <DateRange start={data.task.startDate} end={data.task.endDate} />
        <Tag label={data.task.type} style={data.task.type} />
        <Tag label={data.task.extra} style={data.task.extra} />
      </div>
      <div className="mt-6">
        <Typography tag="h4" type="softHeader" addClass="py-2 mt-2">
          {data.task.description}
        </Typography>

        {data.task.details && (
          <Typography tag="p" type="label">
            {data.task.details}
          </Typography>
        )}
      </div>
      <div className="my-5">
        <Tabs tabs={tabs} />
      </div>
      <div className="mt-auto">
        <CommentForm
          onSubmit={handleComment}
          allowAttachments={2}
          loading={loading}
        />
      </div>
    </div>
  );
};
export default ProjectTaskDetail;
